/**
 * Signal sonore d'attention, à la manière de Windows : un « ding » bref quand l'utilisateur
 * clique au mauvais endroit (bouton ou champ désactivé, en dehors d'une fenêtre ouverte),
 * et un son d'alerte quand un message d'erreur s'affiche.
 *
 * Le navigateur ne donne pas accès aux sons du système : le son est synthétisé (Web Audio),
 * proche du son « Par défaut » de Windows. Il peut être coupé dans « Mon compte ».
 */

const STORAGE_KEY = 'gc.sons-attention';
const DISABLED =
  'button:disabled, input:disabled, select:disabled, textarea:disabled, [aria-disabled="true"], [data-disabled="true"]';

let context: AudioContext | null = null;
let lastPlayed = 0;

export function soundsEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setSoundsEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
  } catch {
    /* stockage indisponible : réglage limité à la session */
  }
}

function audio(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  context ??= new Ctor();
  if (context.state === 'suspended') void context.resume();
  return context;
}

/** Deux notes brèves et amorties (« ding ») ; « erreur » : plus grave, en deux temps. */
export function playAttention(kind: 'ding' | 'erreur' = 'ding', { force = false } = {}): void {
  if (!force && !soundsEnabled()) return;
  const now = Date.now();
  if (now - lastPlayed < 250) return; // pas de rafale
  lastPlayed = now;
  const ctx = audio();
  if (!ctx) return;
  const notes =
    kind === 'ding'
      ? [
          { freq: 880, start: 0, length: 0.45 },
          { freq: 1318.5, start: 0, length: 0.3 },
        ]
      : [
          { freq: 659.3, start: 0, length: 0.25 },
          { freq: 523.3, start: 0.14, length: 0.4 },
        ];
  const t0 = ctx.currentTime;
  for (const note of notes) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = note.freq;
    const start = t0 + note.start;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.18, start + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + note.length);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + note.length + 0.02);
  }
}

/** La fenêtre (modale) clignote brièvement, comme une boîte de dialogue Windows. */
function flash(element: Element | null): void {
  if (!element) return;
  element.classList.remove('gc-attention');
  void (element as HTMLElement).offsetWidth; // relance l'animation
  element.classList.add('gc-attention');
  window.setTimeout(() => element.classList.remove('gc-attention'), 600);
}

/** Élément désactivé sous le pointeur (les éléments désactivés ne reçoivent pas le clic). */
function disabledAt(x: number, y: number): Element | null {
  for (const el of document.querySelectorAll(DISABLED)) {
    const box = el.getBoundingClientRect();
    if (box.width === 0 || box.height === 0) continue;
    if (x >= box.left && x <= box.right && y >= box.top && y <= box.bottom) return el;
  }
  return null;
}

function onPointerDown(event: PointerEvent): void {
  if (event.button !== 0) return;
  const target = event.target instanceof Element ? event.target : null;
  // Clic en dehors d'une fenêtre ouverte : elle reste ouverte, on prévient.
  if (
    target &&
    !target.closest('.mantine-Modal-content') &&
    target.closest('.mantine-Modal-inner, .mantine-Modal-overlay')
  ) {
    playAttention('ding');
    const windows = document.querySelectorAll('.mantine-Modal-content');
    flash(windows[windows.length - 1] ?? null);
    return;
  }
  if (disabledAt(event.clientX, event.clientY)) playAttention('ding');
}

let installed = false;

/** À appeler une fois au démarrage de l'application. */
export function installAttentionSounds(): () => void {
  if (installed || typeof document === 'undefined') return () => undefined;
  installed = true;
  document.addEventListener('pointerdown', onPointerDown, true);
  return () => {
    document.removeEventListener('pointerdown', onPointerDown, true);
    installed = false;
  };
}
